import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, CheckCircle2, ArrowLeft, Building2, MapPin, FileSearch, Sparkles, Phone, Mail, User } from 'lucide-react';

import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
// V37.08 — Indicatifs téléphoniques internationaux + drapeaux pour UX ultra pro
import { INDICATIFS_TELEPHONIQUES, getFlagUrl, getIndicatifByCode } from '../config/membreFormConfig';

const API = process.env.REACT_APP_BACKEND_URL;

const FONTS = {
  playfair: { fontFamily: '"Playfair Display", Georgia, serif' },
  outfit: { fontFamily: '"Outfit", system-ui, sans-serif' },
  body: { fontFamily: '"IBM Plex Sans", system-ui, sans-serif' },
};

const FALLBACK_TYPES = [
  { value: 'standard', label: 'Lead Standard' },
  { value: 'information', label: 'Information générale' },
];

const TERRITOIRES = [
  'Guadeloupe', 'Martinique', 'Guyane', 'La Réunion', 'Mayotte',
  'Saint-Martin', 'Saint-Barthélemy', 'Nouvelle-Calédonie',
  'Polynésie française', 'Wallis-et-Futuna', 'Métropole', 'Autre',
];

const initialState = {
  type_demande: '',
  territoire: '',
  territoire_autre: '',
  nom: '',
  prenom: '',
  email: '',
  indicatif_pays: '+590', // V37.08 — défaut Guadeloupe (DOM)
  telephone: '',
  raison_sociale: '',
  siret: '',
  code_postal: '',
  ville: '',
  message: '',
};

export default function DemandePublique() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // { reference, tarif }
  const [typesDemande, setTypesDemande] = useState(FALLBACK_TYPES);

  // Charger dynamiquement les types depuis la configuration admin (tarifs_demandes)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/demandes-publiques/types-disponibles`);
        if (!cancelled && Array.isArray(res.data?.types) && res.data.types.length) {
          setTypesDemande(res.data.types);
        }
      } catch {
        // Conserve le fallback
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.type_demande) e.type_demande = 'Sélectionnez un type';
    if (!form.territoire) e.territoire = 'Sélectionnez un territoire';
    if (form.territoire === 'Autre' && !form.territoire_autre.trim()) {
      e.territoire_autre = 'Précisez votre territoire';
    }
    if (!form.nom.trim()) e.nom = 'Nom requis';
    if (!form.prenom.trim()) e.prenom = 'Prénom requis';
    if (!form.email.trim()) e.email = 'Email requis';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email invalide';
    if (!form.telephone.trim()) e.telephone = 'Téléphone requis';
    if (!form.raison_sociale.trim()) e.raison_sociale = 'Raison sociale requise';
    if (!form.siret.trim()) e.siret = 'SIRET requis';
    else if (!/^\d{14}$/.test(form.siret.replace(/\s/g, ''))) e.siret = 'SIRET = 14 chiffres';
    if (!form.message.trim()) e.message = 'Message requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error('Merci de compléter les champs obligatoires');
      return;
    }
    setLoading(true);
    try {
      const territoireFinal =
        form.territoire === 'Autre' ? form.territoire_autre.trim() : form.territoire;
      // V37.08 — Compose le téléphone complet avec indicatif international
      const indicatif = (form.indicatif_pays || '+590').trim();
      const phoneClean = (form.telephone || '').trim();
      const telephoneComplet = phoneClean ? `${indicatif} ${phoneClean}`.trim() : '';
      const payload = {
        ...form,
        siret: form.siret.replace(/\s/g, ''),
        telephone: telephoneComplet,
        territoire: territoireFinal,
        region: territoireFinal,
        entreprise: form.raison_sociale,
      };
      delete payload.territoire_autre;
      delete payload.indicatif_pays;
      const res = await axios.post(`${API}/api/demandes-publiques`, payload);
      setDone({
        reference: res.data?.reference || res.data?.id?.slice(0, 8)?.toUpperCase(),
        tarif: res.data?.tarif,
      });
      toast.success('Demande envoyée — un COOPER vous contactera prochainement');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Une erreur est survenue, merci de réessayer.';
      toast.error(typeof msg === 'string' ? msg : 'Erreur lors de l’envoi');
    } finally {
      setLoading(false);
    }
  };

  const selectedType = useMemo(
    () => typesDemande.find((t) => t.value === form.type_demande),
    [form.type_demande, typesDemande],
  );

  // ----- Écran de confirmation -----
  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 relative overflow-hidden" style={FONTS.body}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.08),transparent_60%),radial-gradient(circle_at_bottom_left,rgba(11,60,93,0.12),transparent_60%)]" />
        <div className="relative max-w-2xl mx-auto px-4 py-16">
          <Card className="border-slate-200 shadow-2xl rounded-3xl overflow-hidden" data-testid="demande-publique-success">
            <div
              className="px-8 py-10 text-white"
              style={{ background: 'linear-gradient(135deg,#0a1e3f 0%,#0B3C5D 100%)' }}
            >
              <div className="h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mb-6" />
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 backdrop-blur flex items-center justify-center">
                  <CheckCircle2 className="w-9 h-9 text-amber-400" />
                </div>
              </div>
              <p className="text-center text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-2">
                Communityplace COOPER'S
              </p>
              <h1
                className="text-center text-3xl sm:text-4xl font-bold"
                style={FONTS.playfair}
              >
                Votre demande est enregistrée
              </h1>
              <div className="h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mt-6" />
            </div>
            <CardContent className="p-8 space-y-6 text-slate-700">
              <p>
                Merci&nbsp;! Votre demande a été transmise à notre réseau de
                <strong> COOPER'S certifié·e·s</strong>. Vous recevrez un email de
                confirmation et serez contacté·e directement par le·la professionnel·le
                le·la plus adapté·e à votre projet.
              </p>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">
                  Référence de votre demande
                </p>
                <p
                  className="text-2xl font-bold tracking-[0.2em] text-slate-900"
                  data-testid="demande-publique-reference"
                >
                  {done.reference || '—'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl border-slate-300"
                  onClick={() => navigate('/')}
                  data-testid="demande-publique-back-home"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
                </Button>
                <Button
                  className="flex-1 rounded-xl text-white"
                  style={{ background: '#0a1e3f' }}
                  onClick={() => {
                    setDone(null);
                    setForm(initialState);
                  }}
                  data-testid="demande-publique-new"
                >
                  Déposer une autre demande
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ----- Formulaire principal -----
  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden" style={FONTS.body}>
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.10),transparent_55%),radial-gradient(circle_at_bottom_left,rgba(11,60,93,0.10),transparent_55%)]" />

      {/* Bandeau institutionnel */}
      <div className="relative" style={{ background: 'linear-gradient(135deg,#0a1e3f 0%,#0B3C5D 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-white">
          <div className="h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mb-6" />
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 text-sm mb-6"
            data-testid="demande-publique-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <p className="text-[10px] tracking-[0.3em] text-amber-400 uppercase">
            Communityplace COOPER'S · O'SCOP Outremer
          </p>
          <h1
            className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight"
            style={FONTS.playfair}
          >
            Déposer une demande
          </h1>
          <p className="mt-4 max-w-2xl text-slate-300 text-base">
            Décrivez votre besoin&nbsp;: nous le transmettons à notre réseau de
            COOPER'S certifié·e·s sur votre territoire. Un·e professionnel·le
            vous contactera rapidement.
          </p>
          <div className="h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mt-8" />
        </div>
      </div>

      {/* Formulaire */}
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 sm:-mt-12 pb-20">
        <Card className="border-slate-200 shadow-2xl rounded-3xl overflow-hidden">
          <CardContent className="p-6 sm:p-10">
            <form onSubmit={submit} className="space-y-8" data-testid="demande-publique-form">
              {/* SECTION 1 — Type & Territoire */}
              <section className="space-y-4">
                <SectionTitle index="01" title="Votre besoin" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Type de demande *" error={errors.type_demande}>
                    <Select
                      value={form.type_demande}
                      onValueChange={(v) => update('type_demande', v)}
                    >
                      <SelectTrigger
                        data-testid="demande-publique-type-trigger"
                        className="rounded-xl border-slate-300 h-12"
                      >
                        <SelectValue placeholder="Sélectionnez un type" />
                      </SelectTrigger>
                      <SelectContent>
                        {typesDemande.map((t) => (
                          <SelectItem key={t.value} value={t.value} data-testid={`demande-publique-type-${t.value}`}>
                            <span className="inline-flex items-center gap-2">
                              {t.icon ? (
                                <span className="text-base leading-none">{t.icon}</span>
                              ) : (
                                <Sparkles className="w-4 h-4 text-amber-500" />
                              )}
                              {t.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Territoire / DOM-TOM *" error={errors.territoire}>
                    <Select
                      value={form.territoire}
                      onValueChange={(v) => update('territoire', v)}
                    >
                      <SelectTrigger
                        data-testid="demande-publique-territoire-trigger"
                        className="rounded-xl border-slate-300 h-12"
                      >
                        <SelectValue placeholder="Sélectionnez votre territoire" />
                      </SelectTrigger>
                      <SelectContent>
                        {TERRITOIRES.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`demande-publique-territoire-${t}`}>
                            <span className="inline-flex items-center gap-2">
                              <MapPin className="w-4 h-4" /> {t}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                {form.territoire === 'Autre' && (
                  <Field label="Précisez votre territoire *" error={errors.territoire_autre} icon={MapPin}>
                    <Input
                      data-testid="demande-publique-territoire-autre"
                      value={form.territoire_autre}
                      onChange={(e) => update('territoire_autre', e.target.value)}
                      placeholder="Ex. Saint-Pierre-et-Miquelon, Maroc, Sénégal…"
                      className="rounded-xl border-slate-300 h-12"
                    />
                  </Field>
                )}
                {selectedType && (
                  <p className="text-xs text-slate-500" style={FONTS.outfit}>
                    Vous demandez&nbsp;: <strong className="text-slate-700">{selectedType.label}</strong>
                  </p>
                )}
              </section>

              <Divider />

              {/* SECTION 2 — Coordonnées */}
              <section className="space-y-4">
                <SectionTitle index="02" title="Vos coordonnées" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Nom *" error={errors.nom} icon={User}>
                    <Input
                      data-testid="demande-publique-nom"
                      value={form.nom}
                      onChange={(e) => update('nom', e.target.value)}
                      className="rounded-xl border-slate-300 h-12"
                    />
                  </Field>
                  <Field label="Prénom *" error={errors.prenom} icon={User}>
                    <Input
                      data-testid="demande-publique-prenom"
                      value={form.prenom}
                      onChange={(e) => update('prenom', e.target.value)}
                      className="rounded-xl border-slate-300 h-12"
                    />
                  </Field>
                  <Field label="Email *" error={errors.email} icon={Mail}>
                    <Input
                      data-testid="demande-publique-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      className="rounded-xl border-slate-300 h-12"
                    />
                  </Field>
                  <Field label="Téléphone *" error={errors.telephone} icon={Phone}>
                    {/* V37.08 — Champ téléphone pro avec drapeau + indicatif */}
                    <div className="flex gap-2">
                      <Select
                        value={form.indicatif_pays || '+590'}
                        onValueChange={(value) => update('indicatif_pays', value)}
                      >
                        <SelectTrigger
                          data-testid="demande-publique-indicatif"
                          className="w-[130px] rounded-xl border-slate-300 h-12"
                        >
                          <SelectValue>
                            {(() => {
                              const ind = getIndicatifByCode(form.indicatif_pays || '+590') || INDICATIFS_TELEPHONIQUES[1];
                              return (
                                <span className="flex items-center gap-2">
                                  <img
                                    src={getFlagUrl(ind.flag)}
                                    alt={ind.name}
                                    className="w-5 h-4 object-cover rounded-sm"
                                  />
                                  <span className="text-sm font-medium">{ind.code}</span>
                                </span>
                              );
                            })()}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[260px]">
                          {INDICATIFS_TELEPHONIQUES.map((ind, idx) => (
                            <SelectItem key={`${ind.code}-${ind.flag}-${idx}`} value={ind.code}>
                              <span className="flex items-center gap-2">
                                <img src={getFlagUrl(ind.flag)} alt={ind.name} className="w-5 h-4 object-cover rounded-sm" />
                                <span className="text-sm">{ind.code} · {ind.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        data-testid="demande-publique-telephone"
                        value={form.telephone}
                        onChange={(e) => {
                          // Garde uniquement chiffres + espaces
                          const v = e.target.value.replace(/[^\d\s]/g, '');
                          update('telephone', v);
                        }}
                        placeholder="6 90 12 34 56"
                        inputMode="tel"
                        className="flex-1 rounded-xl border-slate-300 h-12"
                      />
                    </div>
                  </Field>
                </div>
              </section>

              <Divider />

              {/* SECTION 3 — Entreprise */}
              <section className="space-y-4">
                <SectionTitle index="03" title="Votre entreprise" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Raison sociale *" error={errors.raison_sociale} icon={Building2}>
                    <Input
                      data-testid="demande-publique-raison-sociale"
                      value={form.raison_sociale}
                      onChange={(e) => update('raison_sociale', e.target.value)}
                      className="rounded-xl border-slate-300 h-12"
                    />
                  </Field>
                  <Field label="SIRET *" error={errors.siret} icon={FileSearch}>
                    <Input
                      data-testid="demande-publique-siret"
                      value={form.siret}
                      onChange={(e) => update('siret', e.target.value)}
                      placeholder="14 chiffres"
                      maxLength={17}
                      className="rounded-xl border-slate-300 h-12"
                    />
                  </Field>
                  <Field label="Code postal" icon={MapPin}>
                    <Input
                      data-testid="demande-publique-code-postal"
                      value={form.code_postal}
                      onChange={(e) => update('code_postal', e.target.value)}
                      className="rounded-xl border-slate-300 h-12"
                    />
                  </Field>
                  <Field label="Ville" icon={MapPin}>
                    <Input
                      data-testid="demande-publique-ville"
                      value={form.ville}
                      onChange={(e) => update('ville', e.target.value)}
                      className="rounded-xl border-slate-300 h-12"
                    />
                  </Field>
                </div>
              </section>

              <Divider />

              {/* SECTION 4 — Message */}
              <section className="space-y-4">
                <SectionTitle index="04" title="Décrivez votre projet" />
                <Field label="Message *" error={errors.message}>
                  <Textarea
                    data-testid="demande-publique-message"
                    rows={6}
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    placeholder="Détaillez votre besoin (nature du chantier, garantie souhaitée, délais, contexte ESS…)"
                    className="rounded-xl border-slate-300"
                  />
                </Field>
              </section>

              {/* Submit */}
              <div className="pt-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
                <p className="text-xs text-slate-500 max-w-md" style={FONTS.outfit}>
                  En envoyant ce formulaire, un compte prospect léger est créé
                  automatiquement pour suivre votre demande. Vos données restent
                  protégées (RGPD).
                </p>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="demande-publique-submit"
                  className="rounded-xl px-8 py-6 text-white font-semibold shadow-lg"
                  style={{ background: 'linear-gradient(135deg,#0a1e3f 0%,#0B3C5D 100%)' }}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? 'Envoi en cours…' : 'Envoyer ma demande'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------- Composants internes ----------

function SectionTitle({ index, title }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="text-[10px] tracking-[0.3em] uppercase font-semibold text-amber-500"
        style={FONTS.outfit}
      >
        {index}
      </span>
      <h2
        className="text-lg sm:text-xl font-semibold text-slate-900"
        style={FONTS.playfair}
      >
        {title}
      </h2>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-slate-100" />;
}

function Field({ label, error, icon: Icon, children }) {
  return (
    <div className="space-y-1.5">
      <Label
        className="text-xs uppercase tracking-wider text-slate-600 font-semibold"
        style={FONTS.outfit}
      >
        <span className="inline-flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />} {label}
        </span>
      </Label>
      {children}
      {error && (
        <p className="text-[11px] text-red-600 font-medium">{error}</p>
      )}
    </div>
  );
}
